/**
 * PKCE for the CLI side of the OAuth handoff.
 *
 * The CLI generates a random verifier and sends only its SHA-256 challenge to
 * `initiate`; the verifier never leaves the machine and is what `poll` uses to
 * claim the session. Mirrors the worker's `pkce.ts`.
 *
 * @module
 */
import { Effect } from "effect"

const toBase64Url = (bytes: Uint8Array): string => {
  let binary = ""
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

/** A high-entropy, opaque PKCE verifier. */
export const generateVerifier = (): string =>
  `${crypto.randomUUID()}${crypto.randomUUID()}`.replace(/-/g, "")

/** `base64url(SHA-256(verifier))` — the PKCE S256 challenge. */
export const codeChallengeOf = (verifier: string): Effect.Effect<string> =>
  Effect.promise(() =>
    crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)),
  ).pipe(Effect.map((digest) => toBase64Url(new Uint8Array(digest))))
